import { Component, EventEmitter, Input, OnInit, OnDestroy, Output } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { map, Observable, Subject, takeUntil } from 'rxjs';
import { FormUtility } from '../../../shared/forms/form-utility';
import { ReferenceCache } from '../../reference/services/reference.cache';
import { ComponentOptions } from '../models/component-option';
import { ClaimsAppSubmissionConstant } from '../models/claims-app-submission.constant';

@Component({
  selector: 'claims-app-coverage-component-selection',
  templateUrl: './claims-app-coverage-component-selection.component.html',
  styleUrl: './claims-app-coverage-component-selection.component.scss'
})
export class ClaimsAppCoverageComponentSelectionComponent implements OnInit, OnDestroy {
  @Input({ required: true}) form: FormGroup;
  @Output() onSelected = new EventEmitter<ComponentOptions>();

  private readonly _destroying$ = new Subject<void>();

  public filteredOptions: Observable<ComponentOptions[]>;  
  public componentOptions: ComponentOptions[] = [];
  
  public componentOptionFormControl: FormControl<ComponentOptions>;
  
  public get isLoading(): boolean {
    return this.referenceCache.updatingComponents;
  }

  constructor(private referenceCache: ReferenceCache) {
  }

  ngOnInit(): void {
    this.referenceCache.componentsSubject
        .pipe(takeUntil(this._destroying$))
        .subscribe(result => {
          this.componentOptions = result;
    });

    this.componentOptionFormControl = new FormControl<ComponentOptions>(null, [this.requireSelectionValidator()]);
    this.form?.addControl('component', this.componentOptionFormControl);
    
    // Set filtering for component options autocomplete.
    this.filteredOptions = this.componentOptionFormControl.valueChanges
      .pipe(map(value => this.filterComponentOptions(value))
    );
  }
  
  ngOnDestroy(): void {    
		this._destroying$.next(undefined);
		this._destroying$.complete();
  }
  
  /**
   * Filter Component options based on the value entered in the input field.
   */
  private filterComponentOptions(value: string | ComponentOptions): ComponentOptions[] {
    let filterValue: string = '';
    if (typeof value === 'string') {
      filterValue = value;  // Don't trim here, allow multi-word input
    } else {
      filterValue = value.name;
    }
    let results = this.componentOptions.filter(option => option.name.toLowerCase().includes(filterValue.toLowerCase()));

    if (results.length === 0) {
      // Create custom option with original input (will be trimmed when selected)
      results.push({ 
        name: filterValue, 
        groupName: ClaimsAppSubmissionConstant.createNewComponentLabel, 
        code: '' 
      });
    }

    return results;
  }

  /**
   * What to display in the input field when an option is selected.
   */ 
  public displayPartOption(option: ComponentOptions): string {
    return option ? option.name : '';
  }

  public onComponentSelected(): void {
    const selectedValue = this.form.get('component').value as ComponentOptions;
    if (selectedValue?.groupName === ClaimsAppSubmissionConstant.createNewComponentLabel) {
      // Trim the name and update the form control
      this.componentOptionFormControl.setValue(
        {
          ...selectedValue,
          name: selectedValue.name.trim()
        },
        { emitEvent: false });
    }

    this.onSelected.emit(selectedValue);
    this.form.get('component').disable();
  }
  
  public clearComponent(): void {
    FormUtility.clear(this.form, 'component');
    this.form.get('component').enable();
    
    this.onSelected.emit(null);
  }  
  /**
   * Gets a value indicating if the specified field has a value.
   * @param field The form field to check.
   * @returns If the field has a value, `true`; otherwise, `false`.
   */
  public hasValue(field: string): boolean {
    return FormUtility.hasValue(this.form, field)
  }

  /**
   * Validator to ensure that a selection has been made from the component options.
   * If the value is an object (selected from the list), it is valid.
   * If the value is a string (not selected), it is invalid.
   */
  public requireSelectionValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (typeof control.value === 'object' && control.value !== null) {
        return null;
      }
      return { requiredSelection: true };
    };
  }
}
